/**
 * 通用价格输入框
 * 1、金额限制 0 - 99999.99
 * 2、提示校验错误
 */
import React, { useState, useEffect } from 'react';
import { Input, InputProps } from 'antd';
import cls from 'classnames';

import { format2Decimal } from '@/utils/number';

import { validateInputValue } from './helper';
import styles from './index.module.scss';

export interface NumberInputProps
  extends Pick<InputProps, Exclude<keyof InputProps, 'onChange' | 'value'>> {
  value: string;
  onChange: (value: string) => void;
  keepInput?: boolean; // 一直是input
}

const Index: React.FC<React.PropsWithChildren<NumberInputProps>> = (props) => {
  const { value, onChange, disabled, keepInput, children, ...extra } = props;
  const [isFocus, setFocusStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // 校验输入
  const checkInputValue = (valueStr) => {
    setErrorMessage(validateInputValue(valueStr));
  };

  // 输入回调
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (disabled) return;
    const inputStr = event.target.value;
    checkInputValue(inputStr);
    onChange(inputStr);
  };

  // 输入框聚焦
  const handleInputFocus = () => {
    setFocusStatus(true);
  };

  // 输入失去焦点
  const handleInputBlur = () => {
    setFocusStatus(false);
    if (!errorMessage) {
      onChange(value ? format2Decimal(value) : value || '');
    }
  };

  // 点击编辑
  const handleViewClick = () => {
    if (disabled) return;
    handleInputFocus();
  };

  // 渲染输入框
  const renderInputNode = () => {
    if (isFocus || errorMessage || keepInput) {
      // 聚焦 或者 输入错误时 或 一直保持input的场景
      return (
        <Input
          autoFocus
          {...extra}
          prefix={extra.prefix || ''}
          value={value}
          disabled={disabled}
          onChange={handleChange}
        />
      );
    }
    return (
      <div onClick={handleViewClick} className={styles.readOnlyValue}>
        <pre>{value || ' '}</pre>
      </div>
    );
  };

  // 值改变时 重新校验
  useEffect(() => {
    if (!isFocus && errorMessage) {
      // 输入框未聚焦 且有错误的时候 需要重新校验
      checkInputValue(value);
    }
  }, [value]);

  return (
    <div
      className={cls(styles.priceInputContainer, {
        [styles.inputErrorStatus]: errorMessage,
        [styles.normalInput]: keepInput,
        [styles.inputDisabledStatus]: disabled,
      })}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
    >
      <div className={styles.inputWrapper}>
        <div className={styles.inputContent}>{renderInputNode()}</div>
        {children}
      </div>
      <div className={styles.errorHintWrapper}>{errorMessage}</div>
    </div>
  );
};

export default Index;
